import { useState, useEffect, useMemo, useRef } from 'react'
import { getBrands, getFeed, getUserSizes } from '../api'
import { useAuth } from '../context/AuthContext'
import { useProfiles } from '../context/ProfileContext'
import AlertModal from '../components/AlertModal'
import './Listings.css'
import SuggestBrandModal from '../components/SuggestBrandModal'

const CATEGORY_GROUPS = [
    {
        label: 'Tops', value: 'tops',
        children: [
            { label: 'T-Shirt', value: 'tshirt' },
            { label: 'Casual Shirt', value: 'casual_shirt' },
            { label: 'Dress Shirt', value: 'dress_shirt' },
            { label: 'Polo', value: 'polo' },
            { label: 'Sweater', value: 'sweater' },
            { label: 'Hoodie & Sweatshirt', value: 'hoodie' },
            { label: 'Activewear Tops', value: 'activewear_tops' },
            { label: 'Other Tops', value: 'other_tops' },
        ]
    },
    {
        label: 'Outerwear', value: 'outerwear',
        children: [
            { label: 'Coats, Jackets & Vests', value: 'coats_jackets' },
            { label: 'Activewear Jackets', value: 'activewear_jackets' },
            { label: 'Tracksuits & Sets', value: 'tracksuits' },
        ]
    },
    {
        label: 'Bottoms', value: 'bottoms',
        children: [
            { label: 'Pants', value: 'pants' },
            { label: 'Jeans', value: 'jeans' },
            { label: 'Activewear Pants', value: 'activewear_pants' },
            { label: 'Shorts', value: 'shorts' },
            { label: 'Activewear Shorts', value: 'activewear_shorts' },
            { label: 'Swimwear', value: 'swimwear' },
        ]
    },
    {
        label: 'Footwear', value: 'footwear',
        children: [
            { label: 'Sneakers', value: 'sneakers' },
            { label: 'Boots', value: 'boots' },
            { label: 'Casual Shoes', value: 'casual_shoes' },
            { label: 'Dress Shoes & Loafers', value: 'dress_shoes_loafers' },
        ]
    },
    {
        label: 'Other', value: 'other',
        children: [
            { label: 'Other Activewear', value: 'other_activewear' },
            { label: 'Suits', value: 'suits' },
            { label: 'Underwear', value: 'underwear' },
            { label: 'Sleepwear', value: 'sleepwear' },
            { label: 'Socks', value: 'socks' },
        ]
    },
]

const CONDITION_OPTIONS = [
    { label: 'New', value: 'new' },
    { label: 'Pre-owned', value: 'pre-owned' },
]

const APPAREL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL']
const WAIST_SIZES = [28, 30, 32, 34, 36, 38, 40, 42, 44]
const INSEAM_SIZES = [28, 30, 32, 34, 36]
const SHOE_SIZES = [7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 13]
const STAR_OPTIONS = [5, 4, 3, 2, 1]

function formatListedDate(isoDate) {
    const listed = new Date(isoDate)
    const now = new Date()
    const diffMs = now - listed
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays <= 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    const diffWeeks = Math.floor(diffDays / 7)
    if (diffWeeks === 1) return '1w ago'
    return `${diffWeeks}w ago`
}

function ListingCard({ listing, userSizes }) {
    const brand = listing.brands || {}
    const sizes = listing.sizes || (listing.size_raw ? [listing.size_raw] : [])
    const urls = listing.listing_urls || {}

    // Find the user's matched size for this listing category
    const matchedSize = (() => {
        if (!userSizes || !sizes.length) return null
        const cat = listing.category || ''
        let savedSize = null
        if (cat === 'shoe' || cat === 'sneakers' || cat === 'boots' || cat === 'casual_shoes' || cat === 'dress_shoes_loafers') {
            savedSize = userSizes.shoe_size
        } else if (cat === 'pant' || cat === 'pants' || cat === 'jeans' || cat === 'shorts' || cat === 'swimwear' || cat === 'bottoms' || cat === 'activewear_pants' || cat === 'activewear_shorts') {
            savedSize = userSizes.bottoms_size || (userSizes.pants_waist ? `${userSizes.pants_waist}` : null)
        } else if (cat === 'jacket' || cat === 'coats_jackets' || cat === 'activewear_jackets' || cat === 'tracksuits' || cat === 'outerwear') {
            savedSize = userSizes.outerwear_size
        } else {
            savedSize = userSizes.shirt_size
        }
        if (!savedSize) return null
        return sizes.find(s => s && s.toString().toLowerCase() === savedSize.toString().toLowerCase()) || null
    })()

    // Determine card URL — matched size URL, or first URL, or default
    const cardUrl = (() => {
        if (matchedSize && urls[matchedSize]) return urls[matchedSize]
        const firstSize = sizes[0]
        if (firstSize && urls[firstSize]) return urls[firstSize]
        return listing.listing_url
    })()

    // Build display sizes — matched size first, then up to 4 others, then "+N more"
    const displaySizes = (() => {
        if (!sizes.length) return null
        const MAX = 5
        let ordered = [...sizes]
        if (matchedSize) {
            ordered = [matchedSize, ...sizes.filter(s => s !== matchedSize)]
        }
        const shown = ordered.slice(0, MAX)
        const remaining = ordered.length - MAX
        return { shown, remaining, matchedSize }
    })()

    return (
        <a
            href={cardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="listing-card"
        >
            <div className="listing-image-wrap">
                {listing.image_url ? (
                    <img src={listing.image_url} alt={listing.title} className="listing-image" />
                ) : (
                    <div className="listing-image-fallback">No image</div>
                )}
            </div>
            <div className="listing-info">
                <div className="listing-brand-row">
                    <span className="listing-brand-name">{brand.name}</span>
                    {listing.listed_date && (
                        <span className="listing-date">{formatListedDate(listing.listed_date)}</span>
                    )}
                </div>
                <p className="listing-title">{listing.title}</p>
                <div className="listing-meta-row">
                    <span className="listing-price">
                        {listing.price ? `$${Number(listing.price).toFixed(0)}` : ''}
                    </span>
                    {listing.condition && <span className="listing-condition">{listing.condition}</span>}
                </div>
                {displaySizes ? (
                    <div className="listing-sizes">
                        {displaySizes.shown.map((s, i) => (
                            <span key={s}>
                                {i > 0 && <span className="listing-sizes-dot"> · </span>}
                                <span className={s === displaySizes.matchedSize ? 'listing-size-matched' : ''}>
                                    {s}
                                </span>
                            </span>
                        ))}
                        {displaySizes.remaining > 0 && (
                            <span className="listing-sizes-more"> +{displaySizes.remaining}</span>
                        )}
                    </div>
                ) : (
                    <div className="listing-sizes listing-sizes-unknown">No size listed</div>
                )}
            </div>
        </a>
    )
}

// Generic compact multi-select dropdown: shows a summary label when collapsed,
// expands to a checklist of options.
function MultiSelectDropdown({ label, options, selected, onChange, renderOption }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const toggle = (value) => {
        if (selected.includes(value)) {
            onChange(selected.filter(v => v !== value))
        } else {
            onChange([...selected, value])
        }
    }

    return (
        <div className="multiselect" ref={ref}>
            <button type="button" className={`multiselect-trigger ${selected.length ? 'has-selection' : ''}`} onClick={() => setOpen(o => !o)}>
                {label}
                <span className="multiselect-arrow">▾</span>
            </button>
            {open && (
                <div className="multiselect-menu">
                    {options.map(opt => (
                        <label key={opt.value} className="multiselect-option">
                            <input
                                type="checkbox"
                                checked={selected.includes(String(opt.value))}
                                onChange={() => toggle(String(opt.value))}
                            />
                            {renderOption ? renderOption(opt) : opt.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    )
}

// Brand dropdown: multi-select brands plus a star-rating sub-filter
function BrandMultiSelect({ watchlistBrands, otherBrands, selectedBrandIds, setSelectedBrandIds, selectedStars, setSelectedStars, getBrandRating, user, onSuggest }) {
    const [open, setOpen] = useState(false)
    const [brandSearch, setBrandSearch] = useState('')
    const ref = useRef(null)

    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setBrandSearch('') }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const toggleBrand = (id) => {
        if (selectedBrandIds.includes(id)) {
            setSelectedBrandIds(selectedBrandIds.filter(v => v !== id))
        } else {
            setSelectedBrandIds([...selectedBrandIds, id])
        }
    }

    const toggleStar = (star) => {
        if (selectedStars.includes(star)) {
            setSelectedStars(selectedStars.filter(v => v !== star))
        } else {
            setSelectedStars([...selectedStars, star])
        }
    }

    const searchLower = brandSearch.trim().toLowerCase()
    const filteredWatchlist = searchLower ? watchlistBrands.filter(b => b.name.toLowerCase().includes(searchLower)) : watchlistBrands
    const filteredOther = searchLower ? otherBrands.filter(b => b.name.toLowerCase().includes(searchLower)) : otherBrands

    return (
        <div className="multiselect" ref={ref}>
            <button type="button" className={`multiselect-trigger ${(selectedBrandIds.length || selectedStars.length) ? 'has-selection' : ''}`} onClick={() => setOpen(o => !o)}>
                Brands
                <span className="multiselect-arrow">▾</span>
            </button>
            {open && (
                <div className="multiselect-menu multiselect-menu-wide">
                    <div className="multiselect-search-wrap">
                        <input
                            type="text"
                            className="multiselect-search"
                            placeholder="Search brands..."
                            value={brandSearch}
                            onChange={e => setBrandSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {filteredWatchlist.length > 0 && user && !searchLower && (
                        <>
                            <div className="multiselect-group-label">Filter by rating</div>
                            {STAR_OPTIONS.map(star => (
                                <label key={star} className="multiselect-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedStars.includes(star)}
                                        onChange={() => toggleStar(star)}
                                    />
                                    {'★'.repeat(star)}{'☆'.repeat(5 - star)}
                                </label>
                            ))}
                            <div className="multiselect-divider" />
                        </>
                    )}
                    {filteredWatchlist.length > 0 && (
                        <>
                            {!searchLower && <div className="multiselect-group-label">Your saved brands</div>}
                            {filteredWatchlist.map(b => (
                                <label key={b.id} className="multiselect-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedBrandIds.includes(b.id)}
                                        onChange={() => toggleBrand(b.id)}
                                    />
                                    {b.name}
                                </label>
                            ))}
                        </>
                    )}
                    {filteredOther.length > 0 && (
                        <>
                            {!searchLower && <div className="multiselect-group-label">Other brands</div>}
                            {filteredOther.map(b => (
                                <label key={b.id} className="multiselect-option">
                                    <input
                                        type="checkbox"
                                        checked={selectedBrandIds.includes(b.id)}
                                        onChange={() => toggleBrand(b.id)}
                                    />
                                    {b.name}
                                </label>
                            ))}
                        </>
                    )}
                    {searchLower && filteredWatchlist.length === 0 && filteredOther.length === 0 && (
                        <div className="multiselect-empty">
                            No brands match
                            {onSuggest && (
                                <button type="button" className="suggest-search-hint" onClick={() => onSuggest(brandSearch)}>
                                    Suggest "{brandSearch.trim()}"
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}


// Category dropdown with nested subcategories
function CategoryDropdown({ selectedCategories, setSelectedCategories }) {
    const [open, setOpen] = useState(false)
    const [expandedParents, setExpandedParents] = useState([])
    const ref = useRef(null)

    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const allChildValues = (group) => group.children.map(c => c.value)

    const toggleParent = (group) => {
        const children = allChildValues(group)
        if (children.length === 0) {
            if (selectedCategories.includes(group.value)) {
                setSelectedCategories(selectedCategories.filter(v => v !== group.value))
            } else {
                setSelectedCategories([...selectedCategories, group.value])
            }
        } else {
            const allSelected = children.every(v => selectedCategories.includes(v))
            if (allSelected) {
                // Deselect all children
                setSelectedCategories(selectedCategories.filter(v => !children.includes(v)))
            } else {
                // Select all children — expand is separate, don't auto-expand here
                setSelectedCategories([...new Set([...selectedCategories, ...children])])
            }
        }
    }

    const toggleChild = (group, childValue) => {
        if (selectedCategories.includes(childValue)) {
            setSelectedCategories(selectedCategories.filter(v => v !== childValue))
        } else {
            setSelectedCategories([...selectedCategories, childValue])
        }
    }

    const toggleExpand = (e, groupValue) => {
        e.stopPropagation()
        setExpandedParents(prev =>
            prev.includes(groupValue) ? prev.filter(p => p !== groupValue) : [...prev, groupValue]
        )
    }

    const isParentChecked = (group) => {
        if (group.children.length === 0) return selectedCategories.includes(group.value)
        return allChildValues(group).every(v => selectedCategories.includes(v))
    }

    const isParentIndeterminate = (group) => {
        if (group.children.length === 0) return false
        const children = allChildValues(group)
        const count = children.filter(v => selectedCategories.includes(v)).length
        return count > 0 && count < children.length
    }

    return (
        <div className="multiselect" ref={ref}>
            <button
                type="button"
                className={`multiselect-trigger ${selectedCategories.length ? 'has-selection' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                Category
                <span className="multiselect-arrow">▾</span>
            </button>
            {open && (
                <div className="multiselect-menu">
                    {CATEGORY_GROUPS.map(group => {
                        const hasChildren = group.children.length > 0
                        const expanded = expandedParents.includes(group.value)
                        const checked = isParentChecked(group)
                        const indeterminate = isParentIndeterminate(group)
                        return (
                            <div key={group.value}>
                                <label className="multiselect-option category-parent-option">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        ref={el => { if (el) el.indeterminate = indeterminate }}
                                        onChange={() => toggleParent(group)}
                                    />
                                    <span className="category-parent-label">{group.label}</span>
                                    {hasChildren && (
                                        <button
                                            type="button"
                                            className="category-expand-btn"
                                            onClick={(e) => toggleExpand(e, group.value)}
                                            aria-label={expanded ? 'Collapse' : 'Expand'}
                                        >
                                            {expanded ? '▲' : '▼'}
                                        </button>
                                    )}
                                </label>
                                {hasChildren && expanded && (
                                    <div className="category-children">
                                        {group.children.map(child => (
                                            <label key={child.value} className="multiselect-option category-child-option">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCategories.includes(child.value)}
                                                    onChange={() => toggleChild(group, child.value)}
                                                />
                                                {child.label}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

const FILTERS_STORAGE_KEY = 'listingsFilters'

function loadStoredFilters() {
    try {
        // URL params from alert email links take priority over sessionStorage
        const urlParams = new URLSearchParams(window.location.search)
        const urlBrandIds = urlParams.get('brand_id')
        const urlCategories = urlParams.get('category')
        const urlCondition = urlParams.get('condition')
        const urlQ = urlParams.get('q')

        if (urlBrandIds || urlCategories || urlCondition || urlQ) {
            return {
                selectedBrandIds: urlBrandIds ? urlBrandIds.split(',').filter(Boolean) : [],
                selectedCategories: urlCategories ? urlCategories.split(',').filter(Boolean) : [],
                selectedConditions: urlCondition ? [urlCondition] : [],
                searchQuery: urlQ || '',
            }
        }

        const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch {
        return {}
    }
}

function Listings() {
    const { user } = useAuth()
    const { likedBrandIds, activeProfile, defaultProfile, getBrandRating } = useProfiles()
    const [allBrands, setAllBrands] = useState([])
    const [userSizes, setUserSizes] = useState([])
    const [listings, setListings] = useState([])
    const [loading, setLoading] = useState(true)
    const [showSuggest, setShowSuggest] = useState(false)
    const [suggestTerm, setSuggestTerm] = useState('')

    const [selectedBrandIds, setSelectedBrandIds] = useState(() => loadStoredFilters().selectedBrandIds || [])
    const [selectedStars, setSelectedStars] = useState(() => loadStoredFilters().selectedStars || [])
    const [selectedApparelSizes, setSelectedApparelSizes] = useState(() => loadStoredFilters().selectedApparelSizes || [])
    const [selectedWaistSizes, setSelectedWaistSizes] = useState(() => loadStoredFilters().selectedWaistSizes || [])
    const [selectedInseamSizes, setSelectedInseamSizes] = useState(() => loadStoredFilters().selectedInseamSizes || [])
    const [selectedShoeSizes, setSelectedShoeSizes] = useState(() => loadStoredFilters().selectedShoeSizes || [])
    const [selectedCategories, setSelectedCategories] = useState(() => loadStoredFilters().selectedCategories || [])
    const [selectedConditions, setSelectedConditions] = useState(() => loadStoredFilters().selectedConditions || [])
    const [searchQuery, setSearchQuery] = useState(() => loadStoredFilters().searchQuery || '')
    const [matchMySizes, setMatchMySizes] = useState(() => loadStoredFilters().matchMySizes || false)
    const [showAlert, setShowAlert] = useState(false)

    // Persist filter selections across in-app navigation (lost on tab close)
    useEffect(() => {
        const toStore = {
            selectedBrandIds, selectedStars, selectedApparelSizes,
            selectedWaistSizes, selectedInseamSizes, selectedShoeSizes,
            selectedCategories, selectedConditions, searchQuery, matchMySizes,
        }
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(toStore))
    }, [selectedBrandIds, selectedStars, selectedApparelSizes, selectedWaistSizes,
        selectedInseamSizes, selectedShoeSizes, selectedCategories, selectedConditions,
        searchQuery, matchMySizes])

    const [brandsLoaded, setBrandsLoaded] = useState(false)
    const [sizesLoaded, setSizesLoaded] = useState(false)

    useEffect(() => {
        getBrands().then(data => {
            setAllBrands(data || [])
            setBrandsLoaded(true)
        })
    }, [])

    useEffect(() => {
        if (user) {
            getUserSizes(user.id).then(sizes => {
                setUserSizes(sizes || [])
                setSizesLoaded(true)
            })
        } else {
            setSizesLoaded(true)
        }
    }, [user])

    const effectiveProfileId = activeProfile?.id || defaultProfile?.id

    const watchlistBrands = useMemo(
        () => allBrands.filter(b => likedBrandIds.includes(b.id)).sort((a, b) => a.name.localeCompare(b.name)),
        [allBrands, likedBrandIds]
    )

    const otherBrands = useMemo(
        () => allBrands.filter(b => !likedBrandIds.includes(b.id)).sort((a, b) => a.name.localeCompare(b.name)),
        [allBrands, likedBrandIds]
    )

    // Only show "match my sizes" if the user has saved any sizing data
    const hasSavedSizes = userSizes.length > 0

    // Effective brand IDs to query: union of explicitly-selected brands and
    // (if star filters are active) watchlist brands matching those ratings.
    // If no star filter is active, star-based narrowing is skipped entirely
    // (selectedBrandIds alone, or the full watchlist if nothing selected).
    const effectiveBrandIds = useMemo(() => {
        const ids = new Set(selectedBrandIds)
        if (selectedStars.length > 0) {
            watchlistBrands.forEach(b => {
                const rating = getBrandRating(b.id)
                const matchesRating = rating != null && selectedStars.includes(rating)
                if (matchesRating) ids.add(b.id)
            })
        }
        return Array.from(ids)
    }, [selectedBrandIds, selectedStars, watchlistBrands, getBrandRating])

    // Determine active size filter group (apparel/waist/shoe are mutually exclusive groups)
    const { sizeValues, sizeType } = useMemo(() => {
        if (selectedApparelSizes.length > 0) return { sizeValues: selectedApparelSizes, sizeType: 'apparel' }
        if (selectedWaistSizes.length > 0) {
            // Combine each selected waist with each selected inseam (or waist alone if no inseam selected)
            if (selectedInseamSizes.length > 0) {
                const combos = []
                selectedWaistSizes.forEach(w => {
                    selectedInseamSizes.forEach(i => combos.push(`${w}x${i}`))
                })
                return { sizeValues: combos, sizeType: 'waist' }
            }
            return { sizeValues: selectedWaistSizes.map(String), sizeType: 'waist' }
        }
        if (selectedShoeSizes.length > 0) return { sizeValues: selectedShoeSizes.map(String), sizeType: 'shoe' }
        return { sizeValues: [], sizeType: '' }
    }, [selectedApparelSizes, selectedWaistSizes, selectedInseamSizes, selectedShoeSizes])

    useEffect(() => {
        if (!brandsLoaded || !sizesLoaded) return

        if (user && likedBrandIds.length === 0) {
            setListings([])
            setLoading(false)
            return
        }
        setLoading(true)

        if (user && matchMySizes && hasSavedSizes) {
            getFeed(user.id, { profileId: effectiveProfileId, brandIds: effectiveBrandIds, categories: selectedCategories, conditions: selectedConditions, q: searchQuery })
                .then(data => {
                    const sizesByBrand = {}
                    userSizes.forEach(s => { sizesByBrand[s.brand_id] = s })

                    const exactMatch = (raw, candidate) => {
                        if (!raw || !candidate) return false
                        const r = raw.toLowerCase().replace(/\s+/g, '')
                        const c = String(candidate).toLowerCase().replace(/\s+/g, '')
                        const isNumeric = /^[\d.]+$/.test(c)
                        const pattern = isNumeric
                            ? new RegExp(`(?<![\\d.])${c}(?![\\d.])`)
                            : new RegExp(`(?<![a-z])${c}(?![a-z])`)
                        return pattern.test(r)
                    }

                    const filtered = (data || []).filter(listing => {
                        const sizes = sizesByBrand[listing.brand_id]
                        if (!sizes || !listing.size_raw) return false
                        const raw = listing.size_raw.toLowerCase().replace(/\s+/g, '')

                        const noCat = selectedCategories.length === 0
                        const catSet = new Set(selectedCategories)

                        if (sizes.pants_waist && (noCat || catSet.has('pant'))) {
                            const w = String(sizes.pants_waist)
                            if (raw === w || raw.startsWith(`${w}x`)) return true
                        }

                        if (catSet.size === 1) {
                            if (catSet.has('shirt')) return exactMatch(raw, sizes.shirt_size)
                            if (catSet.has('jacket')) return exactMatch(raw, sizes.outerwear_size)
                            if (catSet.has('pant')) return exactMatch(raw, sizes.bottoms_size)
                            if (catSet.has('shoe')) return exactMatch(raw, sizes.shoe_size)
                        }

                        const candidates = [sizes.shirt_size, sizes.outerwear_size, sizes.bottoms_size, sizes.shoe_size]
                        return candidates.some(c => exactMatch(raw, c))
                    })
                    setListings(filtered)
                    setLoading(false)
                })
        } else {
            getFeed(user?.id || '', {
                profileId: effectiveProfileId,
                brandIds: effectiveBrandIds,
                sizes: sizeValues,
                sizeType,
                categories: selectedCategories,
                conditions: selectedConditions,
                q: searchQuery,
            }).then(data => {
                setListings(data || [])
                setLoading(false)
            })
        }
    }, [brandsLoaded, sizesLoaded, user, likedBrandIds, effectiveProfileId, effectiveBrandIds, sizeValues, sizeType, selectedCategories, selectedConditions, searchQuery, matchMySizes, hasSavedSizes, userSizes])

    if (user && likedBrandIds.length === 0) {
        return (
            <div className="listings-page">
                <div className="empty-state">
                    <h2>No listings yet</h2>
                    <p>Save brands on Discover or My Brands to see their latest secondhand finds here.</p>
                </div>
            </div>
        )
    }

    const hasActiveFilters = selectedBrandIds.length > 0 || selectedStars.length > 0 ||
        selectedApparelSizes.length > 0 || selectedWaistSizes.length > 0 ||
        selectedInseamSizes.length > 0 || selectedShoeSizes.length > 0 ||
        selectedCategories.length > 0 || selectedConditions.length > 0 ||
        searchQuery.trim().length > 0 || matchMySizes

    const resetFilters = () => {
        setSelectedBrandIds([])
        setSelectedStars([])
        setSelectedApparelSizes([])
        setSelectedWaistSizes([])
        setSelectedInseamSizes([])
        setSelectedShoeSizes([])
        setSelectedCategories([])
        setSelectedConditions([])
        setSearchQuery('')
        setMatchMySizes(false)
    }

    const noBrandSynced = effectiveBrandIds.length > 0 && listings.length === 0 && !loading

    return (
        <div className="listings-page">
            <div className="page-header">
                <h1>Resale Listings</h1>
                <p>Browse recent resale listings for brands across the Marque Index.</p>
            </div>

            {!user && (
                <div className="listings-signin-banner">
                    Sign in to filter by your saved brands.
                </div>
            )}

            <div className="listings-toolbar-row">
                <div className="listings-search-wrap">
                    <input
                        type="text"
                        className="listings-search"
                        placeholder="Search listings (e.g. blue linen shirt)..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') e.target.blur()
                        }}
                    />
                </div>
                <button
                    type="button"
                    className="listings-alert-btn"
                    onClick={() => setShowAlert(true)}
                >
                    Set alert
                </button>
            </div>

            <div className="listings-filters">
                <BrandMultiSelect
                    watchlistBrands={watchlistBrands}
                    otherBrands={otherBrands}
                    selectedBrandIds={selectedBrandIds}
                    setSelectedBrandIds={setSelectedBrandIds}
                    selectedStars={selectedStars}
                    setSelectedStars={setSelectedStars}
                    getBrandRating={getBrandRating}
                    user={user}
                    onSuggest={(term) => { setSuggestTerm(term); setShowSuggest(true) }}
                />

                <MultiSelectDropdown
                    label="Apparel size"
                    options={APPAREL_SIZES.map(s => ({ label: s, value: s }))}
                    selected={selectedApparelSizes}
                    onChange={(vals) => {
                        setSelectedApparelSizes(vals)
                        if (vals.length) { setSelectedWaistSizes([]); setSelectedInseamSizes([]); setSelectedShoeSizes([]) }
                    }}
                />

                <div className="listings-waist-group">
                    <MultiSelectDropdown
                        label="Waist"
                        options={WAIST_SIZES.map(s => ({ label: `${s}"`, value: s }))}
                        selected={selectedWaistSizes}
                        onChange={(vals) => {
                            setSelectedWaistSizes(vals)
                            if (vals.length) { setSelectedApparelSizes([]); setSelectedShoeSizes([]) }
                            else setSelectedInseamSizes([])
                        }}
                    />
                    <MultiSelectDropdown
                        label="Inseam"
                        options={INSEAM_SIZES.map(s => ({ label: `${s}"`, value: s }))}
                        selected={selectedInseamSizes}
                        onChange={(vals) => {
                            setSelectedInseamSizes(vals)
                            if (vals.length) { setSelectedApparelSizes([]); setSelectedShoeSizes([]) }
                        }}
                    />
                </div>

                <MultiSelectDropdown
                    label="Shoe size"
                    options={SHOE_SIZES.map(s => ({ label: String(s), value: s }))}
                    selected={selectedShoeSizes}
                    onChange={(vals) => {
                        setSelectedShoeSizes(vals)
                        if (vals.length) { setSelectedApparelSizes([]); setSelectedWaistSizes([]); setSelectedInseamSizes([]) }
                    }}
                />

                <CategoryDropdown
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                />

                <MultiSelectDropdown
                    label="Condition"
                    options={CONDITION_OPTIONS}
                    selected={selectedConditions}
                    onChange={setSelectedConditions}
                />

                <button
                    type="button"
                    className={`listings-reset-filters${hasActiveFilters ? '' : ' listings-reset-filters--inactive'}`}
                    onClick={resetFilters}
                    disabled={!hasActiveFilters}
                >
                    Reset filters
                </button>
            </div>

            {hasSavedSizes && (
                <label className="match-sizes-toggle">
                    <input
                        type="checkbox"
                        checked={matchMySizes}
                        onChange={e => {
                            setMatchMySizes(e.target.checked)
                            if (e.target.checked) {
                                setSelectedApparelSizes([]); setSelectedWaistSizes([]); setSelectedInseamSizes([]); setSelectedShoeSizes([])
                            }
                        }}
                    />
                    Show only my sizes
                </label>
            )}

            {!loading && listings.length === 0 ? (
                <div className="empty-state">
                    {noBrandSynced ? (
                        <>
                            <h2>No listings synced for this brand yet</h2>
                            <p>We're steadily adding more brands to the resale feed — check back soon.</p>
                        </>
                    ) : (
                        <>
                            <h2>No listings match right now</h2>
                            <p>Try a different size or category, or check back later — listings refresh regularly.</p>
                        </>
                    )}
                </div>
            ) : (
                <div className={`listings-grid${loading ? ' listings-grid--loading' : ''}`}>
                    {listings.map(listing => (
                        <ListingCard key={listing.id} listing={listing} userSizes={userSizes?.[0] || null} />
                    ))}
                </div>
            )}
            {showAlert && (
                <AlertModal
                    user={user}
                    allBrands={allBrands}
                    onClose={() => setShowAlert(false)}
                />
            )}
        {showSuggest && (
                <SuggestBrandModal
                    initialBrandName={suggestTerm}
                    onClose={() => setShowSuggest(false)}
                />
            )}
        </div>
    )
}

export default Listings
